'use client';

import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, MouseEvent } from 'react';

import type { CourseCardWidgetProps } from '../types/course-card';
import {
  formatCourseCardPrimaryCategory,
  formatCourseCardStartDate,
  formatCourseCardTeachersSummary,
} from '../lib/course-card-formatters';
import { resolveCourseCardHref } from '../lib/course-card-links';
import { useCourseCardInteraction } from './useCourseCardInteraction';
import { useIsDesktopHover } from '@/shared/hooks/useIsDesktopHover';

export function useCourseCardWidget({
  course,
  isFavorite = false,
  isInCart = false,
  isSelected = false,
  selectionValue,
  cartIntent = 'default',
  selectedCourseId,
  onAddToCart,
  onAddToCartAnimationComplete,
  onCheckout,
  onFavoriteToggle,
  onSelectionChange,
  onNavigate,
}: CourseCardWidgetProps) {
  const isDesktopHover = useIsDesktopHover();
  const [imageError, setImageError] = useState(false);
  const [isSelectionChecked, setIsSelectionChecked] = useState(isSelected);
  const shouldNotifyAddAnimationRef = useRef(false);
  const actionContext = { intent: cartIntent, selectedCourseId };

  const { cartItem, cartState, handleCartAction } = useCourseCardInteraction({
    course,
    isInCart,
    onAddToCart: (courseItem, nextCartItem) => {
      shouldNotifyAddAnimationRef.current = true;
      onAddToCart?.(courseItem, nextCartItem, actionContext);
    },
    onCheckout: (courseItem, nextCartItem) =>
      onCheckout?.(courseItem, nextCartItem, actionContext),
  });

  const href = resolveCourseCardHref(course);
  const startLabel = formatCourseCardStartDate(course.startDate, course.modality, course.currency);
  const primaryCategoryLabel = formatCourseCardPrimaryCategory(course.primaryCategory);
  const promoLabel = course.promoLabel?.trim();
  const teachersSummary = formatCourseCardTeachersSummary(course);
  const resolvedSelectionValue = selectionValue ?? course.id;

  useEffect(() => {
    setIsSelectionChecked(isSelected);
  }, [isSelected]);

  const handleFavoriteToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onFavoriteToggle?.(course, !isFavorite);
  };

  const handleCardCartAction = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleCartAction();
  };

  const handleAddToCartAnimationComplete = () => {
    if (!shouldNotifyAddAnimationRef.current) return;

    shouldNotifyAddAnimationRef.current = false;
    onAddToCartAnimationComplete?.(course, cartItem, actionContext);
  };

  const handleSelectionChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const nextSelected = event.currentTarget.checked;

    setIsSelectionChecked(nextSelected);
    onSelectionChange?.(course, nextSelected, resolvedSelectionValue);
  };

  const handleNavigate = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!onNavigate) return;

    event.preventDefault();
    onNavigate(course, href);
  };

  return {
    cartState,
    handleAddToCartAnimationComplete,
    handleCardCartAction,
    handleCartAction,
    handleFavoriteToggle,
    handleNavigate,
    handleSelectionChange,
    href,
    imageError,
    isDesktopHover,
    isSelectionChecked,
    primaryCategoryLabel,
    promoLabel,
    resolvedSelectionValue,
    setImageError,
    startLabel,
    teachersSummary,
  };
}
